import { create } from "zustand";
import {
    clampWorkflowName,
    parseWorkflowToSteps,
    serializeStepsToWorkflow,
    validateWorkflow,
    type ServerWorkflow,
    type StepDefinition,
    type ValidationError,
    type WorkflowUpdatePayload,
} from "../parser";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { ActionNodeData } from "../nodes/ActionNode";
import { Trigger, type TriggerType } from "../nodes/TriggerNode";
import dayjs from "dayjs";

import {
    getWorkflowApiErrorMessage,
    isWorkflowApiLogicalFailure,
    parseFetchDataEnvelope,
    pickWorkflowResponseStatus,
    readWorkflowUpsertFields,
} from "../utils/workflowApiResponse";

type AppProperties = Record<string, unknown>;

type FetchData = (
    url: string,
    method: string,
    payload: unknown,
    appProperties: AppProperties
) => Promise<unknown>;

export type ConditionKind = "WhenToContinue";

export type ActionKind =
    | "AutomatedResponse"
    | "NotifyUser"
    | "CreateLead"
    | "LogInCrm";

const CONDITION_KIND_TITLES: Record<ConditionKind, string> = {
    WhenToContinue: "When to continue",
};

const ACTION_KIND_TITLES: Record<ActionKind, string> = {
    AutomatedResponse: "Automated Response",
    NotifyUser: "Notify User",
    CreateLead: "Create Lead",
    LogInCrm: "Log in CRM",
};

function getNextNumericNodeId(steps: StepDefinition[]): string {
    let max = 0;
    for (const s of steps) {
        const m = /^n(\d+)$/i.exec(s.id);
        if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    return `n${max + 1}`;
}

function makeDefaultTriggerStep(triggerType: TriggerType): StepDefinition {
    return {
        id: "n1",
        type: "trigger",
        data:
            triggerType === Trigger.Message.Outbound
                ? {
                      triggerType: Trigger.Message.Outbound,
                      title: "Outgoing",
                      description: "When an Outgoing Message Is Sent",
                  }
                : {
                      triggerType: Trigger.Message.Inbound,
                      title: "Incoming",
                      description: "When a message comes in",
                  },
    };
}

export interface SaveWorkflowResult {
    success: boolean;
    validationErrors: ValidationError[];
    serverResponse?: unknown;
    /** Server/body error message when success is false for non-validation reasons */
    apiErrorMessage?: string;
}

export interface WorkflowStore {
    steps: StepDefinition[];
    workflowName: string;
    triggerType: TriggerType;

    workflowId: string | null;
    revision: number | null;
    serverStatus: string | null;
    applyOncePerContact: boolean | null;

    selectedNodeId: string | null;
    measuredHeights: Record<string, number>;
    closeSignal: number;
    originalSnapshot: string | null;
    notifyUserStaticMessage: string;
    panelHasDraft: boolean;
    setNotifyUserStaticMessage: (text: string) => void;
    setPanelHasDraft: (v: boolean) => void;

    addStep: (insertIndex: number, nodeType: "condition" | "action") => void;
    deleteStep: (stepId: string) => void;
    updateStepData: (stepId: string, data: Partial<ConditionNodeData> | Partial<ActionNodeData>) => void;
    setWorkflowName: (name: string) => void;
    setApplyOncePerContact: (value: boolean) => void;
    setServerStatus: (status: string | null) => void;

    selectNode: (nodeId: string | null) => void;
    incrementCloseSignal: () => void;
    updateMeasuredHeights: (changes: Array<{ id: string; height: number }>) => void;

    loadFromServer: (serverWorkflow: ServerWorkflow) => void;
    initNewWorkflow: (triggerType: TriggerType) => void;
    discardChanges: () => void;
    saveWorkflow: (deps: { fetchData: FetchData; appProperties: AppProperties }) => Promise<SaveWorkflowResult>;
    setWorkflowEnabled: (
        deps: { fetchData: FetchData; appProperties: AppProperties },
        enabled: boolean
    ) => Promise<{ success: boolean; errorMessage?: string }>;
    clearStore: () => void;
}

const INITIAL_STATE = {
    steps: [] as StepDefinition[],
    workflowName: `Untitled ${dayjs().format("DD/MM/YYYY hh:mm a")}`,
    triggerType: Trigger.Message.Inbound as TriggerType,
    workflowId: null as string | null,
    revision: null as number | null,
    serverStatus: null as string | null,
    applyOncePerContact: null as boolean | null,
    selectedNodeId: null as string | null,
    measuredHeights: {} as Record<string, number>,
    closeSignal: 0,
    originalSnapshot: null as string | null,
    notifyUserStaticMessage: "",
    panelHasDraft: false,
};

function snapshotState(
    steps: StepDefinition[],
    workflowName: string,
    applyOncePerContact: boolean | null
): string {
    return JSON.stringify({ steps, workflowName, applyOncePerContact });
}

type BaselineSnapshotPayload = {
    steps: StepDefinition[];
    workflowName: string;
    applyOncePerContact: boolean | null;
};

function tryParseBaselineSnapshot(snapshotJson: string | null): BaselineSnapshotPayload | null {
    if (snapshotJson == null) return null;
    try {
        const parsed: unknown = JSON.parse(snapshotJson);
        if (typeof parsed !== "object" || parsed === null) return null;
        const record = parsed as Record<string, unknown>;
        const steps = record.steps;
        const workflowName = record.workflowName;
        const applyOncePerContact = record.applyOncePerContact;
        if (!Array.isArray(steps) || steps.length === 0) return null;
        if (typeof workflowName !== "string") return null;
        if (applyOncePerContact != null && typeof applyOncePerContact !== "boolean") return null;
        const hasTrigger = steps.some(
            (step) =>
                typeof step === "object" &&
                step !== null &&
                (step as { type?: string }).type === "trigger"
        );
        if (!hasTrigger) return null;
        return {
            steps: steps as StepDefinition[],
            workflowName,
            applyOncePerContact: applyOncePerContact === undefined ? null : (applyOncePerContact as boolean | null),
        };
    } catch {
        return null;
    }
}

function resolveTriggerTypeFromSteps(steps: StepDefinition[]): TriggerType {
    const triggerStep = steps.find(
        (step): step is StepDefinition & { type: "trigger" } => step.type === "trigger"
    );
    const triggerTypeValue = triggerStep?.data?.triggerType;
    const isOutbound = triggerTypeValue?.toUpperCase().includes("OUTBOUND") ?? false;
    return isOutbound ? Trigger.Message.Outbound : Trigger.Message.Inbound;
}

export const useWorkflowStore = create<WorkflowStore>()((set, get) => ({
    ...INITIAL_STATE,
    steps: [makeDefaultTriggerStep(Trigger.Message.Inbound)],

    setNotifyUserStaticMessage: (text) => {
        set({ notifyUserStaticMessage: text });
    },

    addStep: (insertIndex, nodeType) => {
        const { triggerType } = get();
        if (triggerType === Trigger.Message.Outbound) return;
        set((state) => {
            const next = [...state.steps];
            const newId = getNextNumericNodeId(next);
            next.splice(
                insertIndex,
                0,
                nodeType === "condition"
                    ? { id: newId, type: "condition", data: { isPlaceholder: true } }
                    : { id: newId, type: "action", data: { isPlaceholder: true } },
            );
            return { steps: next, selectedNodeId: newId };
        });
    },

    deleteStep: (stepId) => {
        const { triggerType } = get();
        if (triggerType === Trigger.Message.Outbound) return;
        set((state) => {
            const step = state.steps.find((s) => s.id === stepId);
            if (!step || step.type === "trigger") return state;
            return {
                steps: state.steps.filter((s) => s.id !== stepId),
                selectedNodeId: null,
            };
        });
    },

    updateStepData: (stepId, data) => {
        set((state) => ({
            steps: state.steps.map((s) => {
                if (s.id !== stepId) return s;
                if (s.type === "trigger") return s;
                if (s.type === "condition") {
                    return { ...s, data: { ...s.data, ...(data as Partial<ConditionNodeData>) } };
                }
                return { ...s, data: { ...s.data, ...(data as Partial<ActionNodeData>) } };
            }),
        }));
    },

    setWorkflowName: (name) => {
        set({ workflowName: clampWorkflowName(name) });
    },

    setApplyOncePerContact: (value) => {
        set({ applyOncePerContact: value });
    },

    setServerStatus: (status) => {
        set({ serverStatus: status });
    },

    selectNode: (nodeId) => {
        set({ selectedNodeId: nodeId, panelHasDraft: false });
    },

    setPanelHasDraft: (v) => {
        set({ panelHasDraft: v });
    },

    incrementCloseSignal: () => {
        set((state) => ({ closeSignal: state.closeSignal + 1 }));
    },

    updateMeasuredHeights: (changes) => {
        set((state) => {
            let changed = false;
            const next = { ...state.measuredHeights };
            for (const { id, height } of changes) {
                if (Math.abs((next[id] ?? 0) - height) > 1) {
                    next[id] = height;
                    changed = true;
                }
            }
            if (!changed) return {};
            return { measuredHeights: next };
        });
    },

    loadFromServer: (serverWorkflow) => {
        const parsedSteps = parseWorkflowToSteps(serverWorkflow);
        const isOutbound = serverWorkflow.triggerType?.toUpperCase().includes("OUTBOUND") ?? false;
        const triggerTypeFromServer: TriggerType = isOutbound ? Trigger.Message.Outbound : Trigger.Message.Inbound;
        const workflowName = clampWorkflowName(serverWorkflow.name ?? "Untitled 1");
        const applyOncePerContact = serverWorkflow.applyOncePerContact ?? null;

        if (parsedSteps.length === 0) {
            const defaultTriggerStep = makeDefaultTriggerStep(triggerTypeFromServer);
            const emptySteps = [defaultTriggerStep];
            const preservedNotify = get().notifyUserStaticMessage;
            set({
                ...INITIAL_STATE,
                steps: emptySteps,
                workflowId: serverWorkflow.workflowId ?? null,
                workflowName,
                triggerType: triggerTypeFromServer,
                revision: serverWorkflow.revision ?? null,
                serverStatus: serverWorkflow.status ?? null,
                applyOncePerContact,
                selectedNodeId: null,
                measuredHeights: {},
                originalSnapshot: snapshotState(emptySteps, workflowName, applyOncePerContact),
                notifyUserStaticMessage: preservedNotify,
            });
            return;
        }

        set({
            steps: parsedSteps,
            workflowId: serverWorkflow.workflowId ?? null,
            workflowName,
            triggerType: triggerTypeFromServer,
            revision: serverWorkflow.revision ?? null,
            serverStatus: serverWorkflow.status ?? null,
            applyOncePerContact,
            selectedNodeId: null,
            measuredHeights: {},
            originalSnapshot: snapshotState(parsedSteps, workflowName, applyOncePerContact),
        });
    },

    initNewWorkflow: (triggerType) => {
        const steps = [makeDefaultTriggerStep(triggerType)];
        const preservedNotify = get().notifyUserStaticMessage;
        set({
            ...INITIAL_STATE,
            triggerType,
            steps,
            originalSnapshot: snapshotState(steps, `Untitled ${dayjs().format("DD/MM/YYYY hh:mm a")}`, null),
            notifyUserStaticMessage: preservedNotify,
        });
    },

    discardChanges: () => {
        const state = get();
        const baseline = tryParseBaselineSnapshot(state.originalSnapshot);
        if (!baseline) return;
        const triggerType = resolveTriggerTypeFromSteps(baseline.steps);
        set((s) => ({
            steps: baseline.steps,
            workflowName: clampWorkflowName(baseline.workflowName),
            applyOncePerContact: baseline.applyOncePerContact,
            triggerType,
            selectedNodeId: null,
            measuredHeights: {},
            closeSignal: s.closeSignal + 1,
        }));
    },

    saveWorkflow: async ({ fetchData, appProperties }) => {
        const { steps, workflowName, triggerType, workflowId, applyOncePerContact, serverStatus, revision } = get();

        const validationErrors = validateWorkflow(steps, workflowName);
        if (validationErrors.length > 0) {
            return { success: false, validationErrors };
        }

        const payload = serializeStepsToWorkflow(steps, {
            workflowId: workflowId ?? undefined,
            name: workflowName,
            triggerType: triggerType,
            applyOncePerContact: applyOncePerContact ?? undefined,
            status: serverStatus ?? undefined,
            revision: revision ?? undefined,
        });

        const isUpdate = "workflowId" in payload && !!(payload as WorkflowUpdatePayload).workflowId;
        const apiUrl = isUpdate ? `workflows/${(payload as WorkflowUpdatePayload).workflowId}` : "workflows";
        const httpMethod = isUpdate ? "PUT" : "POST";
        const rawApiResponse = await fetchData(apiUrl, httpMethod, payload, appProperties);

        if (rawApiResponse == null) {
            return {
                success: false,
                validationErrors: [],
                serverResponse: rawApiResponse,
                apiErrorMessage: "Request failed. Please try again.",
            };
        }

        const { axiosBody, entity } = parseFetchDataEnvelope(rawApiResponse);

        if (isWorkflowApiLogicalFailure(axiosBody) || isWorkflowApiLogicalFailure(entity)) {
            return {
                success: false,
                validationErrors: [],
                serverResponse: rawApiResponse,
                apiErrorMessage:
                    getWorkflowApiErrorMessage(entity, axiosBody) ?? "Request failed. Please try again.",
            };
        }

        const upsert = readWorkflowUpsertFields(entity);
        if (upsert.workflowId && !isUpdate) {
            set({
                workflowId: upsert.workflowId,
                revision: upsert.revision ?? 1,
                serverStatus: upsert.status ?? "DRAFT",
            });
        } else if (isUpdate) {
            const patch: { revision?: number; serverStatus?: string } = {};
            if (upsert.revision != null) patch.revision = upsert.revision;
            if (upsert.status) patch.serverStatus = upsert.status;
            if (Object.keys(patch).length > 0) set(patch);
        }
        set((state) => ({
            originalSnapshot: snapshotState(state.steps, state.workflowName, state.applyOncePerContact),
        }));

        return { success: true, validationErrors: [], serverResponse: rawApiResponse };
    },

    setWorkflowEnabled: async ({ fetchData, appProperties }, enabled) => {
        const { workflowId } = get();
        if (!workflowId) {
            return { success: false, errorMessage: "No rule to update." };
        }
        const endpoint = enabled ? "enable" : "disable";
        const serverResponse = await fetchData(
            `workflows/${workflowId}/${endpoint}`,
            "POST",
            null,
            appProperties
        );
        if (serverResponse == null) {
            return { success: false, errorMessage: "Request failed. Please try again." };
        }
        const { axiosBody, entity } = parseFetchDataEnvelope(serverResponse);
        if (isWorkflowApiLogicalFailure(axiosBody) || isWorkflowApiLogicalFailure(entity)) {
            return {
                success: false,
                errorMessage:
                    getWorkflowApiErrorMessage(entity, axiosBody) ?? "Request failed. Please try again.",
            };
        }
        const nextStatus = pickWorkflowResponseStatus(entity, axiosBody) ?? (enabled ? "ACTIVE" : "DISABLED");
        set({ serverStatus: nextStatus });
        return { success: true };
    },

    clearStore: () => {
        const steps = [makeDefaultTriggerStep(Trigger.Message.Inbound)];
        const preservedNotify = get().notifyUserStaticMessage;
        set({
            ...INITIAL_STATE,
            steps,
            originalSnapshot: snapshotState(steps, `Untitled ${dayjs().format("DD/MM/YYYY hh:mm a")}`, null),
            notifyUserStaticMessage: preservedNotify,
        });
    },
}));
export { CONDITION_KIND_TITLES, ACTION_KIND_TITLES };
export type { StepDefinition, ConditionNodeData, ActionNodeData, TriggerType, ServerWorkflow, ValidationError };
